/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import { type ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { token, type ThemeColorModes } from '@atlaskit/tokens';
import { createDiffFromFragments } from '@atlassian/editor-ai-common/diff/createDiffFromFragments';
import { createDiffTransformation } from '@atlassian/editor-ai-common/diff/createDiffTransformation';
import {
	ignoreTrailingAndLeadingSpecialCharacters,
	concatConsecutiveReplacements,
} from '@atlassian/editor-ai-common/diff/postProcessing';

import { PreviewRenderer } from '../components/PreviewRenderer/PreviewRenderer';

const DELETED_COLOR = '#FF8F73';
const ADDED_COLOR = '#79F2C0';
const REPLACED_COLOR = '#FFE380';

const diffHighlightStylesLight = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"span[data-background-custom-color='#FF8F73'], span[data-background-custom-color='#79F2C0'], span[data-background-custom-color='#FFE380']":
		{
			background: `repeating-linear-gradient(
			90deg,
			${token('color.border.accent.gray')},
			${token('color.border.accent.gray')} ${token('space.025')},
			transparent ${token('space.025')},
			transparent ${token('space.050')}) repeat-x left bottom / 100% ${token('space.025')} border-box,
			conic-gradient(
				from var(--panel-gradient-angle, 270deg),
				#0065FF1A 0%,
				#0469FF1A 20%,
				#BF63F31A 50%,
				#FFA9001A 56%,
				#0065FF1A 100%
			) padding-box`,
		},
});

const diffHighlightStylesDark = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"span[data-background-custom-color='#FF8F73'], span[data-background-custom-color='#79F2C0'], span[data-background-custom-color='#FFE380']":
		{
			background: `repeating-linear-gradient(
			90deg,
			${token('color.border.accent.gray')},
			${token('color.border.accent.gray')} ${token('space.025')},
			transparent ${token('space.025')},
			transparent ${token('space.050')}) repeat-x left bottom / 100% ${token('space.025')} border-box,
			conic-gradient(
				from var(--panel-gradient-angle, 270deg),
				#0065FF80 0%,
				#0469FF80 20%,
				#BF63F380 50%,
				#FFA90080 56%,
				#0065FF80 100%
			) padding-box`,
		},
});

const diffDeletedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"span[data-background-custom-color='#FF8F73']": {
		// DELETED_COLOR
		textDecorationLine: 'line-through',
		textDecorationColor: token('color.border.bold'),
		textDecorationThickness: token('space.025'),
	},
});

export const createDiffPreview = ({
	editorView,
	pmFragment,
	positions,
	providerFactory,
	colorMode,
}: {
	editorView: EditorView;
	pmFragment: Fragment;
	positions: [start: number, end: number];
	providerFactory: ProviderFactory;
	colorMode: ThemeColorModes;
}) => {
	if (pmFragment === Fragment.empty) {
		return;
	}

	const { schema } = editorView.state;

	// If schema doesn't have textColor, underline, or strike, we won't be able to render the highlights
	if (!schema.marks?.backgroundColor) {
		return;
	}

	let slice;
	// If the positions are the same, then we know we don't have a normal range
	// selection, so we want to send the full document
	if (positions[0] === positions[1]) {
		slice = editorView.state.doc.slice(0, editorView.state.doc.content.size);
		// If the positions are different then we want to send in the positions as
		// this will be a selection the user made
	} else {
		slice = editorView.state.doc.slice(positions[0], positions[1], true);
	}

	// if the slice starts/ends at different depths, then the selection is across multiple nodes and this can cause issues
	// with the diffing algorithm. We'll just return early here.
	if (slice.openStart !== slice.openEnd) {
		return;
	}

	// Edge case for when the slice is a single line - the content will begin and end
	// with inline nodes - to make it convertable to a document we wrap it in a paragraph
	const content =
		slice.content.firstChild?.isInline && slice.content.lastChild?.isInline
			? Fragment.from(schema.nodes.paragraph.create({}, slice.content))
			: slice.content;

	if (!content) {
		return;
	}

	// IMPORTANT! NOTE!
	// What follows may be a little confusing, but it's necessary to ensure that the final preview displays correctly.
	// The AI can return a fragment with more then just text changes, it can also change the markup of the content.
	// For example, it could bold a word.
	// The diffing alogrithm can't handle this, and is only designed for text changes. So we need to ensure the markup changes
	// are kept in the final preview. To do this we will diff in reverse, and use the final preview fragment as our base
	// to apply diff transformations on top of. This way we can ensure the final preview will display the correct markup changes.
	// This means diffs marked as ADDITION will be displayed as DELETION, and vice versa.
	const diffObjects = createDiffFromFragments({
		original: pmFragment, // This is the final preview fragment
		corrected: content, // this was the original document content. We're diffing in reverse.
		schema,
		offset: 1,
		postProcessors: [
			// Confusable characters could be changed depending on what the AI model was trained on. This will ignore those changes.
			ignoreTrailingAndLeadingSpecialCharacters,
			// We want to avoid have multiple replacements in a row, as it can be confusing to the user. So we'll just join the
			// replacements so they're represented as a single replacement.
			concatConsecutiveReplacements,
		],
	});

	if (!diffObjects.length) {
		return;
	}

	const docFrom = schema.nodes.doc.createAndFill({}, pmFragment);
	if (!docFrom) {
		return;
	}

	const transform = createDiffTransformation(docFrom, diffObjects, {
		onAddition: ({ transform, from, to, replacement }) => {
			if (!replacement) {
				return;
			}
			transform.insert(from, replacement);
			transform.addMark(
				from,
				to + replacement.size,
				// Why deleted color for ADDITION? See important note above.
				schema.marks.backgroundColor.create({ color: DELETED_COLOR }),
			);
		},
		onDeletion: ({ transform, from, to }) => {
			transform.addMark(
				from,
				to,
				schema.marks.backgroundColor.create({
					// Why added color for DELETION? See important note above.
					color: ADDED_COLOR,
				}),
			);
		},
		onReplacement: ({ transform, from, to }) => {
			transform.addMark(
				from,
				to,
				schema.marks.backgroundColor.create({
					color: REPLACED_COLOR,
				}),
			);
		},
	});

	const jsonTransformer = new JSONTransformer(schema);
	let doc: DocNode | undefined;
	try {
		doc = jsonTransformer.encode(transform.doc) as DocNode;
	} catch (e) {
		return;
	}
	if (!doc) {
		return;
	}

	return (
		<div
			data-testid="diff-preview-renderer"
			css={[
				diffDeletedStyles,
				colorMode === 'light' ? diffHighlightStylesLight : diffHighlightStylesDark,
			]}
		>
			<PreviewRenderer
				jsonDocument={doc}
				providerFactory={providerFactory}
				showTelepointer={false}
			/>
		</div>
	);
};
